import React from 'react';
import { Image, ImageStyle, StyleProp, Platform } from 'react-native';
import { useTheme } from '../context/ThemeContext';

// CORE-639 — approved Agapella wordmark (wordmark + small unclosed orbit
// around the last «a», dot top-right). Desktop Web chrome only.
//
// QA CORE-639: use the APPROVED transparent SVGs (no baked background) so the
// single global page background shows through continuously — the opaque PNGs
// showed a coloured rectangle around the mark. RN <Image> can't rasterise SVG,
// so on web we render a raw <img> pointed at the static SVG in /public/brand.
const SVG_LIGHT = '/brand/agapella-logo-approved-light.svg'; // dark wordmark, light theme
const SVG_DARK = '/brand/agapella-logo-approved-dark.svg';   // light wordmark, dark theme

// CORE-1114 — native raster twins of those same SVGs, rendered at 1x/2x/3x with
// a TRANSPARENT background (Metro picks the density suffix automatically). The
// previous `logo_approved_*.png` had the page colour baked in, so on native the
// mark sat in a visible rectangle: those PNGs were cut for the web page
// background (#FFFFFF / #F8F7F5), while the native canvas is warm — #fdf6f3
// light, #202226 dark. Transparent avoids matching a fill to every surface.
const PNG_LIGHT = require('../../assets/brand/logo_wordmark_light.png');
const PNG_DARK = require('../../assets/brand/logo_wordmark_dark.png');
// Ratio of the source SVG (664 × 176) — must track the asset, or the wordmark
// renders squashed. The retired PNGs were 368 × 112.
const PNG_ASPECT = 664 / 176;

interface BrandLogoProps {
  /** Rendered height in px; width follows the asset's own ratio (no squash). */
  height?: number;
  /** Pin a specific theme's asset regardless of the active theme — for
   *  surfaces with a fixed backing (e.g. the main Footer). */
  variant?: 'light' | 'dark';
  style?: StyleProp<ImageStyle>;
}

export const BrandLogo: React.FC<BrandLogoProps> = ({ height = 44, variant, style }) => {
  const { isDark } = useTheme();
  const useDark = variant ? variant === 'dark' : isDark;

  if (Platform.OS === 'web') {
    // Transparent SVG on the global background — no wrapper fill, width:auto so
    // the file's own proportions are preserved (height drives the size, QA §).
    return (
      <img
        src={useDark ? SVG_DARK : SVG_LIGHT}
        alt="Agapella"
        style={{ height, width: 'auto', display: 'block', ...(style as any) }}
      />
    );
  }

  return (
    <Image
      source={useDark ? PNG_DARK : PNG_LIGHT}
      accessibilityLabel="Agapella"
      resizeMode="contain"
      style={[{ height, width: height * PNG_ASPECT }, style]}
    />
  );
};
